import React from "react";
import type {
    InvestmentWithdrawRequest,
    InvestmentWithdrawSummary,
} from "../types/investments";

interface InvestmentWithdrawRequestsProps {
    requests?: InvestmentWithdrawRequest[];
    summary?: InvestmentWithdrawSummary;
    isLoading: boolean;
    onRefresh: () => void;
    onConfirm: (request: InvestmentWithdrawRequest) => void;
    onCancel: (request: InvestmentWithdrawRequest) => void;
    pendingKey?: string | null;
}

const formatCurrency = (value: number): string =>
    `$${value.toFixed(2)}`;

export const InvestmentWithdrawRequests: React.FC<
    InvestmentWithdrawRequestsProps
> = ({
    requests,
    summary,
    isLoading,
    onRefresh,
    onConfirm,
    onCancel,
    pendingKey,
}) => {
    const hasRequests = (requests?.length ?? 0) > 0;

    return (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                        Заявки на вывод (инвестиции)
                    </h2>
                    {summary && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            Всего заявок: {summary.count} · Ожидает:{" "}
                            {formatCurrency(summary.total_pending)} · К
                            зачислению: {formatCurrency(summary.total_net)}
                        </p>
                    )}
                </div>
                <button
                    onClick={onRefresh}
                    className="text-sm px-4 py-2 rounded-md border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                    Обновить
                </button>
            </div>

            {isLoading ? (
                <div className="py-8 text-center text-gray-500 dark:text-gray-400">
                    Загрузка...
                </div>
            ) : !hasRequests ? (
                <div className="py-8 text-center text-gray-500 dark:text-gray-400">
                    Нет заявок в ожидании
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-900">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    Клиент
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    Стратегия
                                </th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    Запрошено
                                </th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    К зачислению
                                </th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    Комиссия
                                </th>
                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    Действие
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {requests?.map((request) => {
                                const key = `${request.user_id}-${request.strategy}`;
                                const disabled = pendingKey === key;
                                const user = request.user;

                                return (
                                    <tr key={key}>
                                        <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">
                                            <div className="font-semibold">
                                                {user?.surname} {user?.name}
                                                <span className="text-gray-500 dark:text-gray-400 text-xs ml-2">
                                                    ID: {request.user_id}
                                                </span>
                                            </div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                                {user?.email || "E-mail не указан"}
                                            </div>
                                            {user?.telegram_id && (
                                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                                    Telegram: {user.telegram_id}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">
                                            <span
                                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                                                style={{
                                                    backgroundColor: `${request.color}15`,
                                                    color: request.color,
                                                }}
                                            >
                                                Стратегия {request.strategy}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 text-sm text-right text-gray-900 dark:text-white">
                                            {formatCurrency(request.pending_amount)}
                                        </td>
                                        <td className="px-4 py-4 text-sm text-right text-gray-900 dark:text-white">
                                            {formatCurrency(request.net_amount)}
                                        </td>
                                        <td className="px-4 py-4 text-sm text-right text-gray-900 dark:text-white">
                                            {formatCurrency(request.commission)}
                                        </td>
                                        <td className="px-4 py-4 text-sm text-center">
                                            <div className="flex items-center justify-center gap-2">
                                                <button
                                                    onClick={() => onConfirm(request)}
                                                    disabled={disabled}
                                                    className={`px-4 py-2 rounded-md text-white text-sm font-medium ${
                                                        disabled
                                                            ? "bg-gray-400 cursor-not-allowed"
                                                            : "bg-green-600 hover:bg-green-700"
                                                    }`}
                                                >
                                                    {disabled
                                                        ? "Обработка..."
                                                        : "Подтвердить"}
                                                </button>
                                                <button
                                                    onClick={() => onCancel(request)}
                                                    disabled={disabled}
                                                    className={`px-4 py-2 rounded-md text-white text-sm font-medium ${
                                                        disabled
                                                            ? "bg-gray-400 cursor-not-allowed"
                                                            : "bg-red-600 hover:bg-red-700"
                                                    }`}
                                                >
                                                    {disabled
                                                        ? "Обработка..."
                                                        : "Отменить"}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

